import { defineComponent, reactive, SetupContext, ref } from '@vue/composition-api';
import CommonMessageDialog from '@/components/common/common-message-dialog.component.vue';
import vueRouter from '@/router';
import SystemConst from '@/shared/system-const';
import HttpStatus from 'http-status-codes';

// リアクティブな変数の型定義
export type reactiveData = {
    isDownloadDialog: boolean;      // ローダー表示切り替え（true: 表示）
    downloadDialogTitle: string;    // ローダーのタイトルメッセージ
    basicFileSize: string;          // 基本データファイルのファイルサイズ
    attachmentFileSize: string;     // 添付データファイルのファイルサイズ
};

/**
 * 年度更新（通知データ作成）：通知データダウンロードコンポーネント
 */
export default defineComponent({
    components: { CommonMessageDialog },
    setup(props, ctx: SetupContext) {
        const commonRef = ref<any>(null);                                               // 共通ダイアログ
        const stepper = <number>3;                                                      // ステッパーの3段階目
        const basicFileName = SystemConst.NOTIFICATION_DATA_FILE_NAME[                  // 基本データファイルのファイル名
            SystemConst.NOTIFICATION_FILE_DOWNLOAD_PARAMETER.BASIC
        ];
        const attachmentFileName = SystemConst.NOTIFICATION_DATA_FILE_NAME[             // 添付データファイルのファイル名
            SystemConst.NOTIFICATION_FILE_DOWNLOAD_PARAMETER.ATTACHMENT
        ];
        const state: reactiveData = reactive<reactiveData>({
            isDownloadDialog: false,
            downloadDialogTitle: SystemConst.FISCAL_YEAR_UPDATE_FILE_DOWNLOAD_DIALOG_TITLE.LOADING,
            basicFileSize: SystemConst.FISCAL_YEAR_UPDATE_FILE_DOWNLOAD_FILE_SIZE_INIT,
            attachmentFileSize: SystemConst.FISCAL_YEAR_UPDATE_FILE_DOWNLOAD_FILE_SIZE_INIT
        });

        /**
         * created
         */
        created();
        async function created() {
            // titleIcon変更
            ctx.root.$store.commit('setTitleIcon', 'mdi-file-key-outline');

            // mainTitle変更
            ctx.root.$store.commit('setMainTitle', '通知データ作成');

            // ファイルサイズを取得する
            await getFileSize();
        }

        /**
         * 基本データファイル及び添付データファイルのファイルサイズをback側から取得する
         */
        async function getFileSize() {
            // ローダーを表示する
            state.isDownloadDialog = true;

            // ファイル情報取得処理のAPIを実行する
            const response = await ctx.root.$store.dispatch('notificationData/getUseImportantInformationFileSize');

            // API通信が正常か判別する
            if (response.status !== HttpStatus.OK) {
                // HTTPステータスコードが200ではない場合
                // ローダーを非表示にする
                state.isDownloadDialog = false;

                // HTTPステータスコードが404か判別
                if (response.status === HttpStatus.NOT_FOUND) {
                    // エラーメッセージを表示してファイル選択画面へ遷移する
                    commonRef.value.displayErrorMessageTransitionDialog(response, '/fiscal-year-update/notification-data/upload');
                } else {
                    // HTTPステータスコードが404以外はエラーダイアログを表示、又はエラー画面へ遷移する。
                    commonRef.value.errorDisplayTransitionControl(response);
                }
                return;
            }

            // レスポンスからファイルサイズを取得し、変数にそれぞれ格納する
            state.basicFileSize = response.data.basicFileSize;
            state.attachmentFileSize = response.data.attachmentFileSize;

            // ローダーを非表示にする
            state.isDownloadDialog = false;
        }

        /**
         * 基本データファイル出力処理
         */
        async function outputBasicFile() {
            // ローダーダイアログを表示する
            state.downloadDialogTitle = SystemConst.FISCAL_YEAR_UPDATE_FILE_DOWNLOAD_DIALOG_TITLE.DOWNLOADING;
            state.isDownloadDialog = true;

            // ファイルダウンロード処理のAPIを実行する
            //   パラメータ: SystemConst.NOTIFICATION_FILE_DOWNLOAD_PARAMETER.BASIC
            const response = await ctx.root.$store.dispatch('notificationData/useImportantInformationFileDownload',
                {
                    getFileParameter: SystemConst.NOTIFICATION_FILE_DOWNLOAD_PARAMETER.BASIC
                }
            );

            // API通信が正常か判別する
            if (response.status !== HttpStatus.OK) {
                // ローダーダイアログを非表示にする
                state.isDownloadDialog = false;

                if (response.status === HttpStatus.NOT_FOUND) {
                    // 404エラーのみダイアログを表示する
                    commonRef.value.displayErrorMessageTransitionDialog(response, '/fiscal-year-update/notification-data/upload');
                } else {
                    // HTTPステータスコードが404以外はエラーダイアログを表示、又はエラー画面へ遷移する。
                    commonRef.value.errorDisplayTransitionControl(response);
                }
            }

            // ステータスコードが200の場合
            // ローダーダイアログを非表示にする
            state.isDownloadDialog = false;
        }

        /**
         * 添付データファイル出力処理
         */
        async function outputAttachmentFile() {
            // ローダーダイアログを表示する
            state.downloadDialogTitle = SystemConst.FISCAL_YEAR_UPDATE_FILE_DOWNLOAD_DIALOG_TITLE.DOWNLOADING;
            state.isDownloadDialog = true;

            // ファイルダウンロード処理のAPIを実行する
            //   パラメータ: SystemConst::NOTIFICATION_FILE_DOWNLOAD_PARAMETER.ATTACHMENT
            const response = await ctx.root.$store.dispatch('notificationData/useImportantInformationFileDownload',
                {
                    getFileParameter: SystemConst.NOTIFICATION_FILE_DOWNLOAD_PARAMETER.ATTACHMENT
                }
            );

            // API通信が正常か判別する
            if (response.status !== HttpStatus.OK) {
                // ローダーダイアログを非表示にする
                state.isDownloadDialog = false;

                if (response.status === HttpStatus.NOT_FOUND) {
                    // 404エラーのみダイアログを表示する
                    commonRef.value.displayErrorMessageTransitionDialog(response, '/fiscal-year-update/notification-data/upload');
                } else {
                    // HTTPステータスコードが404以外はエラーダイアログを表示、又はエラー画面へ遷移する。
                    commonRef.value.errorDisplayTransitionControl(response);
                }
            }

            // ステータスコードが200の場合
            // ローダーダイアログを非表示にする
            state.isDownloadDialog = false;
        }

        /**
         * ファイル選択画面へ遷移する
         */
         function navigationUpload() {
            // ファイル選択画面へ遷移する
            vueRouter.push('/fiscal-year-update/notification-data/upload');
        }

        return {
            commonRef,
            stepper,
            basicFileName,
            attachmentFileName,
            state,
            outputBasicFile,
            outputAttachmentFile,
            navigationUpload
        };
    }
});
